import React, { useEffect } from 'react';
import { ContainerComponentProps } from 'types';
import dayjs from 'dayjs';
import { useUserOrderStore } from 'lib/store/userOrderStore';
import {
  LimitedActivityStoreProvider,
  useLimitedActivityStoreContext,
} from 'lib/components/LimitedActivityStoreContext';
import { LIMITED_ACTIVITY_TYPE } from 'lib/constants/common';
import { ComponentData } from './config';
import Image from './Image';
import './index.scss';

function ActivityContainerContent(props: ContainerComponentProps<ComponentData>) {
  const { children, data, commonStyle } = props;
  const { activityDateRange, limitedActivityType } = data;
  const useLimitedActivityStore = useLimitedActivityStoreContext();
  const initLimitedTimeActivityData = useLimitedActivityStore((state) => state.initData);
  const updateDurationInMonths = useLimitedActivityStore((state) => state.updateDurationInMonths);
  const isSubscriber = useUserOrderStore((state) => state.userOrderInfo.isSubscriber);
  const packageOrderTime = useUserOrderStore((state) => state.userOrderInfo.orderTime);

  useEffect(() => {
    if (!limitedActivityType) {
      return;
    }
    // 初始化限时活动配置
    initLimitedTimeActivityData({
      limitedActivityType,
      dateRange: {
        start: activityDateRange?.[0],
        end: activityDateRange?.[1],
      },
    });
    // 计算更新续订月份
    updateDurationInMonths({
      activityStartTime: activityDateRange?.[0],
      activityEndTime: activityDateRange?.[1],
      limitedActivityType,
      packageOrderTime,
      isSubscriber,
    });
  }, [
    activityDateRange,
    initLimitedTimeActivityData,
    limitedActivityType,
    packageOrderTime,
    isSubscriber,
    updateDurationInMonths,
  ]);

  return (
    <div className="limited-activity__container" style={commonStyle}>
      <Image data={data} />
      {children}
    </div>
  );
}

/**
 * 限时活动容器组件 V2
 * 每个容器实例拥有独立的 store，互不干扰
 */
function LimitedActivityContainerV2(props: ContainerComponentProps<ComponentData>) {
  const { data } = props;
  const { activityDateRange, limitedActivityType } = data;
  const [shouldShow, setShouldShow] = React.useState(false);
  const isSubscriber = useUserOrderStore((state) => state.userOrderInfo.isSubscriber);
  const packageOrderTime = useUserOrderStore((state) => state.userOrderInfo.orderTime);

  useEffect(() => {
    // 该段逻辑用于判断组件是否应该显示
    const handleContainerShowStatusByOrderInfo = () => {
      // 在编辑器中不执行隐藏判断
      if (process.env.IS_EDITOR) {
        setShouldShow(true);
        return;
      }

      // 基础条件检查
      if (!activityDateRange?.[0] || !activityDateRange?.[1]) {
        setShouldShow(false);
        return;
      }

      const startDate = dayjs(activityDateRange[0]).startOf('day');
      const endDate = dayjs(activityDateRange[1]).endOf('day');
      const currentTime = dayjs();

      // 未订购用户：活动结束后不展示
      if (!isSubscriber && currentTime.isAfter(endDate)) {
        setShouldShow(false);
        return;
      }

      // 已订购用户的判断逻辑
      if (isSubscriber) {
        // 订购时间晚于活动结束时间不展示
        if (packageOrderTime && dayjs(packageOrderTime).isAfter(endDate)) {
          setShouldShow(false);
          return;
        }

        // 仅新用户活动：订购时间不在活动范围内不展示
        if (limitedActivityType === LIMITED_ACTIVITY_TYPE.USER_ORDER_TIME_NEW && packageOrderTime) {
          const orderTime = dayjs(packageOrderTime);
          if (orderTime.isBefore(startDate) || orderTime.isAfter(endDate)) {
            setShouldShow(false);
            return;
          }
        }
      }
      setShouldShow(true);
    };

    handleContainerShowStatusByOrderInfo();
  }, [activityDateRange, limitedActivityType, isSubscriber, packageOrderTime]);

  if (!shouldShow) {
    return null;
  }

  return (
    <LimitedActivityStoreProvider>
      <ActivityContainerContent {...props} />
    </LimitedActivityStoreProvider>
  );
}

export default LimitedActivityContainerV2;
